import React from "react";

export default function IngredientRow({
  item,
  i,
  recipeIdx,
  onDelete,
  onReplace,
  draggable,
  onDragStart,
  rowBg
}) {
  return (
    <div
      className={`relative sm:grid sm:grid-cols-[2.5rem_1fr_4.5rem_5.5rem_5.5rem] sm:items-center sm:gap-0 gap-1 ${item.available === false ? 'bg-gray-300' : rowBg || ''} border border-blue-100 rounded-2xl shadow-lg bg-white/80 backdrop-blur-md p-4 sm:p-0 sm:border-0 transition-all hover:shadow-xl hover:border-blue-300 mb-3`}
      style={{paddingTop: '8px', paddingBottom: '8px'}}
      draggable={draggable}
      onDragStart={onDragStart}
    >
      {item.available === false && (
        <span className="absolute left-0 top-0 w-full h-full flex items-center justify-center bg-gray-700 bg-opacity-60 text-red-500 font-bold text-xs z-10 pointer-events-none">
          Unavailable
        </span>
      )}
      {/* MOBILE: 3-row stacked layout */}
      <div className="flex flex-col sm:hidden">
        {/* Row 1: icon, image, title */}
        <div className="flex items-center gap-3 mb-2">
          {(() => {
            let cacheKey = item.productId || item.link || item.title.toLowerCase();
            const cached = window._ingredientCache && window._ingredientCache[cacheKey];
            if (cached && cached.timestamp && (Date.now() - cached.timestamp <= 172800000)) {
              return <span title="Up to date" style={{color: 'green', fontSize: '1.2em', marginRight: 4}}>✔️</span>;
            } else {
              return <span title="Needs update" style={{color: 'red', fontSize: '1.2em', marginRight: 4}}>❌</span>;
            }
          })()}
          {item.image && (
            <img src={item.image} alt={item.title} className="w-12 h-12 object-contain rounded-xl shadow-md border border-blue-100" />
          )}
          <span className="flex-1 pl-1 font-semibold text-gray-800 truncate">
            {item.link ? (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">{item.title}</a>
            ) : item.title}
          </span>
        </div>
        {/* Row 2: qty, price */}
        <div className="flex gap-3 mb-2">
          <div className="flex flex-col flex-1">
            <span className="text-xs text-gray-500 font-semibold mb-1">Qty</span>
            <input
              type="number"
              min="1"
              value={item.quantity || 1}
              onChange={e => {
                const qty = Math.max(1, parseInt(e.target.value) || 1);
                if (typeof window.updateIngredientQuantity === 'function') {
                  window.updateIngredientQuantity(recipeIdx, i, qty);
                }
              }}
              className="w-14 border border-blue-200 bg-white/80 rounded-xl text-center text-xs font-semibold shadow-sm focus:ring-2 focus:ring-blue-400 transition-all"
              style={{ fontSize: '1em' }}
            />
          </div>
          <div className="flex flex-col flex-1">
            <span className="text-xs text-gray-500 font-semibold mb-1">Price</span>
            <span className="text-xs font-semibold text-green-700">{item.price}</span>
          </div>
        </div>
        {/* Row 3: actions 50/50 */}
        <div className="flex gap-3">
          <button
            className="bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl w-1/2 h-9 flex items-center justify-center text-xs font-semibold shadow-md hover:from-red-600 hover:to-red-800 transition-all"
            onClick={onDelete}
            title="Delete ingredient"
          >
            ×
          </button>
          <button
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl w-1/2 h-9 flex items-center justify-center text-xs font-semibold shadow-md hover:from-blue-600 hover:to-blue-800 transition-all"
            onClick={onReplace}
            title="Replace ingredient"
          >
            ↺
          </button>
        </div>
      </div>
      {/* DESKTOP: 5-column grid layout */}
      {/* Icon (status) */}
      <div className="hidden sm:flex items-center justify-center">
        {(() => {
          let cacheKey = item.productId || item.link || item.title.toLowerCase();
          const cached = window._ingredientCache && window._ingredientCache[cacheKey];
          if (cached && cached.timestamp && (Date.now() - cached.timestamp <= 172800000)) {
            return <span title="Up to date" style={{color: 'green', fontSize: '1.2em', marginRight: 4}}>✔️</span>;
          } else {
            return <span title="Needs update" style={{color: 'red', fontSize: '1.2em', marginRight: 4}}>❌</span>;
          }
        })()}
      </div>
      {/* Title and image */}
      <div className="hidden sm:flex items-center gap-3">
        {item.image && (
          <img src={item.image} alt={item.title} className="w-12 h-12 object-contain rounded-xl shadow-md border border-blue-100" />
        )}
        <span className="flex-1 font-semibold text-gray-800 truncate">
          {item.link ? (
            <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">{item.title}</a>
          ) : item.title}
        </span>
      </div>
      {/* Qty */}
      <div className="hidden sm:flex flex-col">
        <input
          type="number"
          min="1"
          value={item.quantity || 1}
          onChange={e => {
            const qty = Math.max(1, parseInt(e.target.value) || 1);
            if (typeof window.updateIngredientQuantity === 'function') {
              window.updateIngredientQuantity(recipeIdx, i, qty);
            }
          }}
          className="w-14 border border-blue-200 bg-white/80 rounded-xl text-center text-xs font-semibold shadow-sm focus:ring-2 focus:ring-blue-400 transition-all"
          style={{ fontSize: '1em' }}
        />
      </div>
      {/* Price */}
      <div className="hidden sm:flex flex-col">
        <span className="text-xs sm:text-base font-semibold text-green-700">{item.price}</span>
      </div>
      {/* Actions */}
      <div className="hidden sm:flex gap-3">
        <button
          className="bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl w-9 h-9 flex items-center justify-center text-xs font-semibold shadow-md hover:from-red-600 hover:to-red-800 transition-all"
          onClick={onDelete}
          title="Delete ingredient"
        >
          ×
        </button>
        <button
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl w-9 h-9 flex items-center justify-center text-xs font-semibold shadow-md hover:from-blue-600 hover:to-blue-800 transition-all"
          onClick={onReplace}
          title="Replace ingredient"
        >
          ↺
        </button>
      </div>
    </div>
  );
}
